import { Component, EventEmitter, Output } from '@angular/core';
import { ModalButtons } from '../../../../shared/component/modal-container/modal-container.component';
import {
  CapitalAccountTemplate,
} from '../../../../excel/templates/financial-statement/capital-account/capital-account.template';
import { NOTIFICATIONS, NotificationsService } from '../../../../../core/service/notifications.service';
import * as XLSX from 'xlsx-js-style';
import { InvestorCapitalAccountDTO } from '../../../models/DTOs/capital-account/InvestorCapitalAccountDTO';
import { OrganizationsService } from '../../../../organization/components/service/organizations.service';
import { CapitalAccountService } from '../../../services/capital-account/capital-account.service';
import { InvestorCapitalAccount } from '../../../models/capital-account/InvestorCapitalAccount';

@Component({
  selector: 'app-manage-investors-modal',
  templateUrl: './manage-investors-modal.component.html',
  styleUrls: ['./manage-investors-modal.component.scss'],
})
export class ManageInvestorsModalComponent {
  @Output() closeModalEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() investorsUploaded: EventEmitter<void> = new EventEmitter<void>();
  buttonsTypes: Record<string, ButtonType> = {
    download: 'accent' as ButtonType,
    upload: 'white-green' as ButtonType,
  };

  modalButtons: ModalButtons = {
    close: true,
  };

  bulkUpload = true;
  investors = new UploadedInvestors();
  switcher = 1;
  totalCommitment: number;
  canUpload = false;
  show = {
    valid: true,
    invalid: true,
  };

  private uploadedFile: File | null = null;

  constructor(
    private capitalAccountService: CapitalAccountService,
    private notificationsService: NotificationsService,
    private organizationsService: OrganizationsService,
  ) {
  }

  close(): void {
    this.closeModalEmitter.emit();
  }

  async switchUploadType(): Promise<void> {
    this.bulkUpload = !this.bulkUpload;
    if (this.bulkUpload) {
      this.clearData();
    } else {
      this.investors.all = await this.capitalAccountService.pullInvestorsFromContribution();
      this.modifyAfterImport();
    }
  }

  downloadTemplate(): void {
    const orgID: number = this.organizationsService.selected$.value.organizationId;
    const fundID: string = this.organizationsService.selected$.value.selectedFund.fundId;
    new CapitalAccountTemplate().exportInvestors(this.capitalAccountService.getInvestors(), orgID, fundID);
    this.switchButtonsTypes();
  }

  uploadData(event: any): void {
    if (!event?.target?.files[0]) {
      return;
    }
    this.uploadedFile = event.target.files[0];
    if (this.uploadedFile.size / 1000000 > INVESTORS_MAX_FILE_SIZE_MB) {
      this.notificationsService.error(NOTIFICATIONS.FILE_SIZE_TOO_LARGE);
      this.uploadedFile = null;
      return;
    }
    this.loadFile(this.uploadedFile);
    this.switchButtonsTypes();
  }

  removeInvestor(investor: InvestorCapitalAccountDTO): void {
    this.investors.all.splice(this.investors.all.indexOf(investor), 1);
    this.validateInvestors();
    this.calculateTotalCommitment();
  }

  /**
   * **Warn:** storeInvestors will delete all existing investors for this fs and create new ones
   */
  async storeInvestors(): Promise<void> {
    const existingInvestors = this.capitalAccountService.getInvestors();
    await this.capitalAccountService.deleteInvestors(InvestorCapitalAccount.toDTOList(existingInvestors));
    await this.capitalAccountService.saveCapitalAccountInvestors(this.investors.valid);
    this.investorsUploaded.emit();
    this.close();
  }

  calculateTotalCommitment(): void {
    this.totalCommitment = this.investors.all.reduce((total, investor) => total + investor.commitment, 0);
  }

  removeInvalidInvestors(): void {
    this.investors.all = this.investors.all.filter((investor) => !this.investors.invalid.includes(investor));
    this.investors.invalid = [];
    this.calculateTotalCommitment();
    this.canUpload = this.investors.valid.length > 0;
  }

  private switchButtonsTypes(): void {
    [this.buttonsTypes.upload, this.buttonsTypes.download] = [this.buttonsTypes.download, this.buttonsTypes.upload];
  }

  private loadFile(file: File): void {
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e: any): void => {
      try {
        const binary: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(binary, { type: 'binary' });
        const ws: XLSX.WorkSheet = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws);
        this.validateExcelFile(data);
        this.investors.all = new CapitalAccountTemplate().importInvestors(data);
        this.modifyAfterImport();
      } catch (error) {
        this.notificationsService.error(NOTIFICATIONS.INVESTORS_UPLOAD_ERROR);
        this.clearData();
      }
    };
  }

  private validateExcelFile(data: any[]): void {
    if (data.length > INVESTORS_MAX_ROW_AMOUNT) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_MAX_ROW_AMOUNT_ERROR);
      throw new Error(NOTIFICATIONS.INVESTORS_MAX_ROW_AMOUNT_ERROR.message);
    }
  }

  private clearData(): void {
    this.investors = new UploadedInvestors();
    this.totalCommitment = null;
    this.uploadedFile = null;
  }

  /**
   * Validates the list of investors.
   *
   * The method performs the following checks:
   * - Ensures that the list of investors is not empty.
   * - Checks if the organization and fund IDs in the investor ID match the selected org and fund.
   * - Verifies each investor's ID to match the format "d*-d*-d*".
   * - Ensures the commitment value for each investor is non-negative.
   *
   * If any of the validation checks fail:
   * - The respective investors are added to the `failedInvestors` list.
   * - An error notification is shown to the user.
   * - The method sets `canUpload` as false to prevent further actions.
   *
   * If all validation checks pass for all investors, it sets the `canUpload` flag to true.
   * When `canUpload` is true, the user will be allowed to proceed (e.g., the continue button will be enabled).
   */
  private validateInvestors(): void {
    if (!this.investors.all.length) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_UPLOAD_ERROR);
      throw new Error(NOTIFICATIONS.INVESTORS_UPLOAD_ERROR.message);
    }

    const failedInvestors: InvestorCapitalAccountDTO[] = [];
    const orgId = String(this.organizationsService.selected$.getValue().organizationId);
    const fundId = parseFloat(this.organizationsService.selected$.getValue().selectedFund.fundId);

    for (const investor of this.investors.all) {
      const [invOrgId, invFundId] = investor.code.split('-').slice(0, 2);
      if (invOrgId !== orgId || parseFloat(invFundId) !== fundId) {
        failedInvestors.push(investor);
        continue;
      }

      if (!investor.code.match(INVESTOR_CODE_REGEX)) {
        failedInvestors.push(investor);
        continue;
      }

      if (investor.commitment < 0) {
        failedInvestors.push(investor);
      }
    }
    if (failedInvestors.length) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_UPLOAD_ERROR);
    }
    this.investors.invalid = failedInvestors;
    this.investors.valid = this.investors.all.filter((investor) => !failedInvestors.includes(investor));
    this.canUpload = !this.investors.invalid.length;
  }

  private modifyAfterImport(): void {
    this.capitalAccountService.sortInvestorsByGpStatusAndLastDigits(this.investors.all);
    this.validateInvestors();
    this.calculateTotalCommitment();
  }
}
export const INVESTORS_MAX_ROW_AMOUNT = 10000;
export const INVESTORS_MAX_FILE_SIZE_MB = 2;
export const INVESTOR_CODE_REGEX = /^\d+-(\d+|\d+\.\d+)-\d+$/;

class UploadedInvestors {
  all: InvestorCapitalAccountDTO[] = [];
  valid: InvestorCapitalAccountDTO[] = [];
  invalid: InvestorCapitalAccountDTO[] = [];
}
export type ButtonType = 'edit' | 'cancel' | 'apply' | 'copy' | 'delete';
